

import React, { Component } from 'react';
import { VIDEO_OUTPUT_STATUS } from '@shootsta/common-domain-data';
import screenfull from 'screenfull';

import cx from 'classnames';
import _get from 'lodash.get';
import uuid from 'uuid/v4';
import { Icon } from '../../../ui-kit';

import {
  assetHasError,
  hasTranscodedVideoFiles,
  isAssetMissing,
  getAnalytics,
  setAnalytics,
  downloadFile,
  parseJSON } from
'../../../common/utils';
import SKIP_AMOUNT from '../constants/skipAmount';
import PlayerContextMenu from './PlayerContextMenu';
import PlayerResizer from './PlayerResizer';
import Controls from './Controls';
import SeekBar from './SeekBar';
import { CommentsOverlay as CommentsOverlayComponent } from '../../Comments';
import attemptToPlayVideo from '../../../common/utils/attemptToPlayVideo';
import PlayerContent from './PlayerContent';
import { KEY_CODES } from '../constants/keyCodes';
import { getSourceFile } from '../utils';
import { removeCommentsPosition } from '../../Comments/components/CommentsOverlay/utils/commentsTransformPosition';
import SKIP_METHODS from '../constants/skipMethods';
import SKIP_FORWARD_BACKWARD_SECONDS from '../constants/skipForwardBackwardSeconds';
import SPEEDS from '../constants/speeds';

import '../styles/_player.scss';

const TRANSCODE_FAILED = 'TRANSCODE_FAILED';







































































class PlayerComponent extends Component {




  _overlayRef = React.createRef();

  _hasAppliedStartTime = false;







  constructor(props) {
    super(props);

    const { showComments, startTime } = props;

    if (showComments) {
      this.CommentsOverlay = CommentsOverlayComponent;
    }

    this.playerId = uuid();

    // A positive `startTime` means this player is replacing a previous one for
    // a different source (e.g. switching video review versions) and should
    // resume from where the viewer was, rather than restarting at 0. The
    // `#t=` media fragment appended by `getSource` avoids a flash of frame 0;
    // `stopBuffering` then applies the clamped seek once we know the duration.
    const resumeFrom = startTime > 0 ? startTime : 0;

    this.state = {
      isPlaying: false,
      isFullscreen: false,
      currentTime: resumeFrom,
      playFromWhenBuffered: resumeFrom || void 0,
      volume: 1,
      totalDuration: 0,
      isHD: true,
      isCommentsEnabled: true,
      buffering: true,
      captionSourceFiles: {},
      controlsVisible: true,
      hoveringVideo: true,
      speed: SPEEDS.x10,
      analyticsEventInProgress: false,
      isCaptionsLoaded: false,
      bynderAudioSourceUrl: '',
      minimisedWidth: 0,
      minimisedHeight: 0
    };
  }

  componentDidMount() {
    const { selectedComment } = this.props;
    this.attemptToSubscribeAsset();
    this.attemptToGetBynderAudioUrl();

    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener(
      'mozfullscreenchange',
      this.handleFullscreenChange
    );
    document.addEventListener(
      'MSFullscreenChange',
      this.handleFullscreenChange
    );
    document.addEventListener(
      'webkitfullscreenchange',
      this.handleFullscreenChange
    );
    document.addEventListener(
      'playerstarted',
      this.handleGlobalPlayerStart
    );

    const minWidth = this._playerRef ? this._playerRef.offsetWidth : 0;
    const minHeight = this._playerRef ? this._playerRef.offsetHeight : 0;

    this.setState({
      minimisedWidth: minWidth,
      minimisedHeight: minHeight
    });

    if (this._overlayRef.current) {
      this._overlayRef.current.style.width = `${minWidth}px`;
      this._overlayRef.current.style.height = `${minHeight}px`;
    }

    const { timestamp: customTime = '' } = selectedComment || {};
    if (customTime || customTime === 0) {
      this.setCustomTime(customTime);
    }
  }

  componentDidUpdate({
    assetId: prevAssetId,
    asset: prevAsset,
    selectedComment: prevSelectedComment




  }) {
    const { assetId, asset, selectedComment } = this.props;
    const { timestamp: customTime = '' } = selectedComment || {};
    const { timestamp: prevCustomTime = '' } = prevSelectedComment || {};

    if (prevCustomTime !== customTime && (customTime || customTime === 0)) {
      this.handleJumpToTimeStamp(customTime);
    }

    if (asset && !prevAsset) {
      this.fetchCaptions(asset);
    } else if (asset && prevAsset) {
      const prevCaptions = prevAsset.assetFiles?.VTT_CAPTION || [];
      const currCaptions = asset.assetFiles?.VTT_CAPTION || [];

      if (prevCaptions.length !== currCaptions.length ||
      currCaptions.some((c, i) => c.sourceUrl !== prevCaptions[i]?.sourceUrl)) {
        this.fetchCaptions(asset);
        this.updateSelectedCaption(currCaptions);
      }
    }

    if (prevAssetId === assetId) {
      return;
    }

    this.attemptToSubscribeAsset();
    this.attemptToGetBynderAudioUrl();
  }

  componentWillUnmount() {
    removeCommentsPosition();
    document.removeEventListener(
      'fullscreenchange',
      this.handleFullscreenChange
    );
    document.removeEventListener(
      'webkitfullscreenchange',
      this.handleFullscreenChange
    );
    document.removeEventListener(
      'mozfullscreenchange',
      this.handleFullscreenChange
    );
    document.removeEventListener(
      'MSFullscreenChange',
      this.handleFullscreenChange
    );
    document.removeEventListener(
      'playerstarted',
      this.handleGlobalPlayerStart
    );
  }

  handleJumpToTimeStamp = (customTime) => {
    if (this._videoRef) {
      this._videoRef.pause();
      this.setState({ isPlaying: false });
      this.setCustomTime(customTime);
    }
  };

  handleFullscreenChange = () => {
    this.setState({ isFullscreen: document.fullscreenElement != null });

    if (!this._overlayRef.current || !this._playerRef) return;

    const { minimisedWidth, minimisedHeight } = this.state;

    const playerWidth = this._playerRef.offsetWidth;
    const playerHeight = this._playerRef.offsetHeight;

    const scaleX = playerWidth / minimisedWidth;
    const scaleY = playerHeight / minimisedHeight;

    const newScale = Math.min(scaleX, scaleY);

    if (this._overlayRef.current) {
      this._overlayRef.current.style.transform = `scale(${newScale})`;
    }
  };

  handleGlobalPlayerStart = (event) => {
    if (event.detail && event.detail.playerId !== this.playerId) {
      const { isPlaying } = this.state;
      if (isPlaying && this._videoRef) {
        this._videoRef.pause();
        this.setState({ isPlaying: false });
      }
    }
  };

  sendAnalytics = async (analyticsType) => {
    const { analyticsResourceId, createAnalyticsEntry } = this.props;
    const { analyticsEventInProgress } = this.state;
    if (createAnalyticsEntry) {
      if (!analyticsResourceId || getAnalytics(analyticsType) || analyticsEventInProgress) {
        return void 0;
      }
      this.setState({ analyticsEventInProgress: true });
      const { error, data } = await createAnalyticsEntry(analyticsType);
      this.setState({ analyticsEventInProgress: false });

      if (error || !data || !data.createAnalyticsEntry) {
        return void 0;
      }

      setAnalytics(analyticsType);
    }
  };

  addCaptionToState = (vtt, caption) => {
    if (!vtt) {
      return;
    }

    this.setState(({ captionSourceFiles }) => ({
      captionSourceFiles: {
        ...captionSourceFiles,
        [caption.assetFileId]: caption.sourceUrl
      }
    }));
  };

  fetchCaptions = (asset) => {
    if (
    !asset ||
    !asset.assetFiles.VTT_CAPTION ||
    !asset.assetFiles.VTT_CAPTION.length)
    {
      return;
    }

    asset.assetFiles.VTT_CAPTION.forEach((caption) => {
      const client = new XMLHttpRequest();

      client.open('GET', caption.sourceUrl);

      client.onreadystatechange = () => {
        const { responseText } = client;

        this.addCaptionToState(responseText, caption);
      };

      client.send();
    });
  };

  updateSelectedCaption = (captions) => {
    const { selectedCaption } = this.state;
    if (!selectedCaption) return;
    const updated = captions.find((c) => c.assetFileId === selectedCaption.assetFileId);
    if (updated) {
      this.setState({ selectedCaption: updated });
    }
  };

  onSelectCaption = (caption) => () => {
    this.setState(({ selectedCaption }) => ({
      selectedCaption: selectedCaption === caption ? void 0 : caption,
      isCaptionsLoaded: true
    }));
  };

  attemptToSubscribeAsset = () => {
    const { asset, assetId, subscribeToAssetTranscoded } = this.props;

    if (hasTranscodedVideoFiles(asset)) {
      return;
    }

    if (subscribeToAssetTranscoded) {
      subscribeToAssetTranscoded(assetId);
    }
  };

  attemptToGetBynderAudioUrl = async () => {
    const { asset, getBynderAssetDownloadUrl } = this.props;
    const { metaData, organisationId } = asset || {};
    const { bynderAssetId, type } = parseJSON(metaData);

    if (!bynderAssetId || !getBynderAssetDownloadUrl || !type.includes('audio')) {
      return;
    }

    const { data, error } = await getBynderAssetDownloadUrl({
      bynderAssetId,
      organisationId
    });

    if (error || !data) {
      // eslint-disable-next-line no-console
      console.error('Error fetching bynder preview', error);
      return;
    }

    if (!data.getBynderAssetDownloadUrl) {
      return;
    }

    this.setState({ bynderAudioSourceUrl: data.getBynderAssetDownloadUrl });
  };

  videoEnded = () => {
    if (!this._videoRef) {
      return;
    }

    this._videoRef.pause();
    setTimeout(() => {
      this.setState({ isPlaying: false });
    }, 1000);
  };

  videoWaiting = () => {
    this.setState({ buffering: true, isPlaying: false });
  };

  videoResuming = () => {
    this.setState({ buffering: false, isPlaying: true });
  };

  stopBuffering = () => {
    if (!this._videoRef) {
      return;
    }

    const { duration } = this._videoRef;

    this.setState({
      buffering: false,
      totalDuration: duration
    });

    this.applyStartTime(duration);

    const { isPlaying } = this.state;

    if (!isPlaying || !this._videoRef) {
      return;
    }

    attemptToPlayVideo(this._videoRef);
  };

  setCurrentTime = () => {
    const { isPlaying, buffering, totalDuration } = this.state;

    if (!this._videoRef || !isPlaying || buffering) {
      return;
    }

    const { currentTime } = this._videoRef;

    if (currentTime / totalDuration * 100 > 80) {
      this.sendAnalytics('VIDEO_END');
    }

    this.setState(
      ({ totalDuration: latestTotalDuration, isPlaying: latestIsPlaying }) => ({
        currentTime,
        isPlaying: currentTime >= latestTotalDuration ? false : latestIsPlaying
      })
    );
  };

  // Seeks to the `startTime` prop once, as soon as the duration is known.
  // Runs only on the first `loadedmetadata` so it never fights the viewer's
  // own scrubbing, or a later source swap such as the HD/SD toggle.
  applyStartTime = (duration) => {
    if (this._hasAppliedStartTime) {
      return;
    }

    this._hasAppliedStartTime = true;

    const { startTime } = this.props;

    if (!(startTime > 0) || !(duration > 0) || !this._videoRef) {
      return;
    }

    // The new source may be shorter than the one we resumed from.
    const resumeTime = Math.min(startTime, duration);

    this._videoRef.currentTime = resumeTime;
    this.setState({ currentTime: resumeTime });
  };

  // The live playhead position, for callers that need it outside of a render
  // (e.g. capturing where the viewer was before swapping the video source).
  getCurrentTime = () => {
    const { currentTime } = this.state;

    return this._videoRef ? this._videoRef.currentTime : currentTime;
  };

  setCustomTime = (time) => {
    const { totalDuration, currentTime } = this.state;
    const minifiedTime = time < 0 ? 0 : time;
    const normalisedTime = minifiedTime > totalDuration ? totalDuration : minifiedTime;

    if (currentTime === normalisedTime) {
      return;
    }

    this.setState({ currentTime: normalisedTime });

    if (!this._videoRef) {
      return;
    }

    this._videoRef.currentTime = normalisedTime;
  };

  noBuffering = () => {
    this.setState({
      buffering: false
    });
  };

  setVideoRef = (videoRef) => {
    if (!videoRef) {
      return;
    }

    if (videoRef.readyState >= 2) {
      this.noBuffering();
    }

    videoRef.addEventListener('ended', this.videoEnded);
    videoRef.addEventListener('timeupdate', this.setCurrentTime);
    videoRef.addEventListener('waiting', this.videoWaiting);
    videoRef.addEventListener('playing', this.videoResuming);
    videoRef.addEventListener('loadedmetadata', this.stopBuffering);

    this._videoRef = videoRef;

    videoRef.focus();
    videoRef.load();
  };

  setPlayerRef = (playerRef) => {
    if (!playerRef) {
      return;
    }

    this._playerRef = playerRef;
  };

  toggleVideoPlay = () => {
    if (!this._videoRef) {
      return;
    }

    const { isPlaying } = this.state;
    const newPlaying = !isPlaying;

    if (newPlaying) {
      const event = new CustomEvent('playerstarted', {
        detail: { playerId: this.playerId }
      });
      document.dispatchEvent(event);

      if (this._videoRef) {
        attemptToPlayVideo(this._videoRef);
      }
      this.sendAnalytics('VIDEO_START');
    } else {
      this._videoRef.pause();
      clearTimeout(this.toggleVideoPlay.stationaryTimeout);
    }

    this.setState({ isPlaying: newPlaying, controlsVisible: true }, () => {
      const {
        isPlaying: currentPlaying,
        hoveringVideo,
        controlsVisible
      } = this.state;

      if (!hoveringVideo) {
        setTimeout(() => {
          if (currentPlaying && controlsVisible && !hoveringVideo) {
            this.setState({ controlsVisible: false });
          }
        }, 750);
        return;
      }

      this.toggleVideoPlay.stationaryTimeout = setTimeout(() => {
        if (currentPlaying && controlsVisible) {
          this.setState({ controlsVisible: false });
        }
      }, 2000);
    });
  };

  handleSkipForwardOrBackward = (action) => {
    const { currentTime } = this.state;

    if (action === SKIP_METHODS.backward) {
      return void this.setCustomTime(currentTime - SKIP_FORWARD_BACKWARD_SECONDS);
    }

    if (action === SKIP_METHODS.forward) {
      return void this.setCustomTime(currentTime + SKIP_FORWARD_BACKWARD_SECONDS);
    }
  };

  handleChangeSpeed = (speed) => {
    if (!this._videoRef) {
      return;
    }

    this._videoRef.playbackRate = speed;

    this.setState({ speed });
  };

  hoverPlayer = () => {
    const { hoveringVideo } = this.state;

    if (hoveringVideo) {
      return;
    }

    document.addEventListener('keydown', this.videoOnKeyDown);
    document.addEventListener('mousemove', this.videoOnMouseMove);

    this.setState({ hoveringVideo: true, controlsVisible: true });
  };

  stopHoverPlayer = () => {
    const { hoveringVideo } = this.state;

    if (!hoveringVideo) {
      return;
    }

    document.removeEventListener('keydown', this.videoOnKeyDown);
    document.removeEventListener('mousemove', this.videoOnMouseMove);
    this.setState({ hoveringVideo: false }, () => {
      setTimeout(() => {
        const {
          isPlaying: currentPlaying,
          hoveringVideo: currentlyHovering,
          controlsVisible
        } = this.state;

        if (currentPlaying && controlsVisible && !currentlyHovering) {
          this.setState({ controlsVisible: false });
        }
      }, 750);
    });
  };

  pauseVideo = () => {
    const { isPlaying } = this.state;

    if (!isPlaying) {
      return;
    }

    this.toggleVideoPlay();
  };

  canFullscreen = () => !!(this._playerRef && screenfull);

  toggleFullscreen = () => {
    if (!this.canFullscreen()) {
      return;
    }

    return void this.setState(
      ({ isFullscreen }) => ({ isFullscreen: !isFullscreen }),
      () => {
        const { isFullscreen } = this.state;

        if (isFullscreen) {
          return void screenfull.request(this._playerRef);
        }

        screenfull.exit();
      }
    );
  };

  toggleComments = () => {
    const { isCommentsEnabled } = this.state;

    this.setState({ isCommentsEnabled: !isCommentsEnabled });
  };

  toggleVolume = () => {
    const { volume } = this.state;

    this.setVolume(volume === 0 ? 0.5 : 0);
  };

  setVolume = (newVolume) => {
    const fixedVolume = parseFloat(newVolume.toFixed(1));

    if (!this._videoRef) {
      return;
    }

    this._videoRef.volume = fixedVolume;

    this.setState({ volume: fixedVolume });
  };

  toggleHD = () => {
    const { isHD, currentTime } = this.state;

    this.setState({
      isHD: !isHD,
      buffering: true,
      playFromWhenBuffered: currentTime
    });
  };

  onClickDownload = () => {
    const { assetId } = this.props;
    downloadFile({ assetId, assetFiles: 'ORIGINAL' });
  };

  videoOnKeyDown = (event) => {
    const { commentModalId } = this.props;

    if (!this._videoRef || commentModalId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const { keyCode } = event;
    const { currentTime, volume } = this.state;

    if (keyCode === KEY_CODES.space) {
      return void this.toggleVideoPlay();
    }

    if (keyCode === KEY_CODES.left) {
      return void this.setCustomTime(currentTime - SKIP_AMOUNT);
    }

    if (keyCode === KEY_CODES.right) {
      return void this.setCustomTime(currentTime + SKIP_AMOUNT);
    }

    if (keyCode === KEY_CODES.m) {
      return void this.toggleVolume();
    }

    if (keyCode === KEY_CODES.up && volume < 1) {
      return void this.setVolume(volume + 0.1);
    }

    if (keyCode === KEY_CODES.down && volume > 0) {
      return void this.setVolume(volume - 0.1);
    }

    if (keyCode === KEY_CODES.f) {
      return void this.toggleFullscreen();
    }

    if (keyCode === KEY_CODES.h) {
      return void this.toggleHD();
    }
  };

  videoOnMouseMove = () => {
    this.setState({ controlsVisible: true });
    clearTimeout(this.toggleVideoPlay.stationaryTimeout);
    clearTimeout(this.videoOnMouseMove.stationaryTimeout);
    this.videoOnMouseMove.stationaryTimeout = setTimeout(() => {
      const {
        isPlaying: currentPlaying,
        controlsVisible
      } = this.state;

      if (currentPlaying && controlsVisible) {
        this.setState({ controlsVisible: false });
      }
    }, 2000);
  };

  getSource = () => {
    const { playFromWhenBuffered, isHD } = this.state;
    const { asset } = this.props;

    if (!asset) {
      return '';
    }

    const { sourceUrl } = getSourceFile({
      asset,
      isHD
    });

    return `${sourceUrl}${
    playFromWhenBuffered ? `#t=${playFromWhenBuffered}` : ''
    }`;
  };

  renderControlBar = () => {
    const {
      isPlaying,
      currentTime,
      totalDuration,
      isFullscreen,
      isCommentsEnabled,
      isHD,
      buffering,
      volume,
      selectedCaption,
      controlsVisible,
      speed,
      isCaptionsLoaded
    } = this.state;

    const {
      asset,
      commentModalId,
      showComments,
      controlsOutside,
      videoStatus,
      videoProduct,
      videoId,
      hideCaptionButton = false,
      loadDefaultCaptions = false,
      isMusicV2 = false,
      showDownload = false,
      onEditCaptionClick,
      isCaptionGenerating,
      hasGenerationError
    } = this.props;
    const loading = asset === void 0;
    const assetMissing = isAssetMissing(asset);
    const assetError = assetHasError(asset);
    const transcoding = !hasTranscodedVideoFiles(asset);
    const transcodeFailed = _get(asset, 'assetFiles.ORIGINAL', {}).status === TRANSCODE_FAILED;
    const disabled = !asset || transcoding || assetMissing || assetError || transcodeFailed;

    return (
      <div
        className={cx('player__floating', {
          'player__floating--hidden':
          (!isFullscreen ? !controlsOutside : true) && !controlsVisible || isMusicV2,
          'player__floating--disabled': disabled,
          'player__floating--inner': !controlsOutside || isFullscreen
        })}>

        <SeekBar
          currentTime={currentTime}
          totalDuration={totalDuration}
          isPlaying={isPlaying}
          onSetManualTime={this.setCustomTime}
          commentModalId={commentModalId}
          isBuffering={buffering}
          loading={loading}
          speed={speed} />


        <Controls
          loading={loading}
          asset={asset}
          currentTime={currentTime}
          toggleVideoPlay={this.toggleVideoPlay}
          toggleFullscreen={this.toggleFullscreen}
          toggleHD={this.toggleHD}
          toggleVolume={this.toggleVolume}
          setVolume={this.setVolume}
          toggleComments={this.toggleComments}
          isPlaying={isPlaying}
          volume={volume}
          isHD={isHD}
          isFullscreen={isFullscreen}
          hdAvailable={Boolean(_get(asset, 'assetFiles.HD'))}
          showComments={showComments}
          isCommentsEnabled={isCommentsEnabled}
          captions={_get(asset, 'assetFiles.VTT_CAPTION', [])}
          onSelectCaption={this.onSelectCaption}
          selectedCaption={selectedCaption}
          videoStatus={videoStatus}
          videoProduct={videoProduct}
          videoId={videoId}
          totalDuration={totalDuration}
          hideCaptionButton={hideCaptionButton}
          showFullscreenButton={this.canFullscreen()}
          onSkipForwardOrBackward={this.handleSkipForwardOrBackward}
          onChangeSpeed={this.handleChangeSpeed}
          loadDefaultCaptions={loadDefaultCaptions}
          isCaptionsLoaded={isCaptionsLoaded}
          showDownload={showDownload}
          onClickDownload={this.onClickDownload}
          onEditCaptionClick={onEditCaptionClick}
          isCaptionGenerating={isCaptionGenerating}
          hasGenerationError={hasGenerationError} />

      </div>);

  };

  render() {
    const {
      isPlaying,
      currentTime,
      totalDuration,
      isCommentsEnabled,
      buffering,
      selectedCaption,
      isFullscreen,
      bynderAudioSourceUrl
    } = this.state;

    const {
      asset,
      customPlaceholder,
      onAddComment,
      onDeleteComment,
      assetId,
      selectedClip,
      showComments,
      filterByClipId,
      controlsOutside,
      matchHeight,
      minWidth,
      minHeight,
      onCommentInputFocus,
      videoStatus,
      standardVideo,
      isAnonymous,
      sceneId,
      shotId,
      isMusicV2 = false,
      bgmImage,
      togglePlaySize,
      isCommentsV2,
      onVisibilityChange,
      selectCommentTab,
      customOverlay
    } = this.props;

    const assetMissing = isAssetMissing(asset);
    const assetError = assetHasError(asset);
    const transcoding = !hasTranscodedVideoFiles(asset);
    const transcodeFailed = _get(asset, 'assetFiles.ORIGINAL', {}).status === TRANSCODE_FAILED;
    const shouldShowBuffering = asset === void 0 || buffering && Boolean(asset) && !transcoding;
    const shouldShowCommentsOverlay = isCommentsEnabled && showComments && asset !== null;
    const isVideoCompleted = videoStatus === VIDEO_OUTPUT_STATUS.APPROVED;
    const disabled = !asset ||
    transcoding ||
    buffering ||
    assetMissing ||
    assetError ||
    transcodeFailed;

    const { CommentsOverlay } = this;

    return (
      <PlayerResizer
        resizable={matchHeight}
        controlsOutside={controlsOutside}
        minWidth={minWidth}
        minHeight={minHeight}
        standardVideo={standardVideo}
        isAnonymous={isAnonymous}
        isFullscreen={isFullscreen}>

        <PlayerContextMenu isDisabled={disabled}>
          <div
            role="button"
            className={cx(
              `player player__control-${
              controlsOutside && !isFullscreen ? 'outside' : 'inside'
              }`,
              {
                'player__standard-video': standardVideo
              }
            )}
            ref={this.setPlayerRef}
            onKeyDown={this.videoOnKeyDown}
            onMouseOver={this.hoverPlayer}
            onFocus={this.hoverPlayer}
            onMouseOut={this.stopHoverPlayer}
            onBlur={this.stopHoverPlayer}
            tabIndex={0}>

            <PlayerContent
              asset={asset}
              isMusicV2={isMusicV2}
              bgmImage={bgmImage}
              customPlaceholder={customPlaceholder}
              selectedCaption={selectedCaption}
              assetError={assetError}
              assetMissing={assetMissing}
              transcodeFailed={transcodeFailed}
              transcoding={transcoding}
              source={bynderAudioSourceUrl || this.getSource()}
              setVideoRef={this.setVideoRef} />


            {customOverlay &&
            <div className="player__custom-overlay-container">
                <div className="player__custom-overlay" ref={this._overlayRef}>
                  {customOverlay}
                </div>
              </div>}


            {
            shouldShowCommentsOverlay && typeof CommentsOverlay !== 'undefined' &&
            <CommentsOverlay
              assetId={assetId}
              sceneId={sceneId}
              shotId={shotId}
              selectedClip={selectedClip}
              pauseTime={this.pauseVideo}
              time={currentTime}
              onAddComment={onAddComment}
              onDeleteComment={onDeleteComment}
              isPlaying={isPlaying}
              totalDuration={totalDuration}
              selector=".player__floating"
              commentsOverlayClass="player__floating__comments"
              setCustomTime={this.setCustomTime}
              filterByClipId={filterByClipId}
              onInputFocus={onCommentInputFocus}
              isVideoCompleted={isVideoCompleted}
              isV2Design={isCommentsV2}
              onVisibilityChange={onVisibilityChange}
              selectCommentTab={selectCommentTab} />}




            {isMusicV2 && !shouldShowBuffering &&
            <Icon
              className="player__music-v2"
              name={isPlaying ? 'pause' : 'play'}
              onClick={this.toggleVideoPlay}
              size={togglePlaySize || 40}
              color="white" />}



            {
            shouldShowBuffering &&
            <Icon
              className="player__buffering"
              size={36}
              name="shootstagrad"
              color="white"
              spin />}




            {(!controlsOutside || isFullscreen) && this.renderControlBar()}
          </div>

          {/*
            Controls are to be rendered below when reviewing video to allow access to comment in control overlay areas in video.
            */}
          {controlsOutside && this.renderControlBar()}
        </PlayerContextMenu>
      </PlayerResizer>);

  }
}

export default PlayerComponent;